import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Inbox, Clock, AlertTriangle, XCircle, CheckCircle, BarChart3, RefreshCw, Download, UserCog, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { formatApiError } from '../lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const HRWorkbench = () => {
  const [activeTab, setActiveTab] = useState('inbox');
  const [pending, setPending] = useState([]);
  const [slaSummary, setSlaSummary] = useState(null);
  const [rejected, setRejected] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lifecycleEvents, setLifecycleEvents] = useState([]);
  const [lifecycleStats, setLifecycleStats] = useState(null);
  const [lifecycleFilter, setLifecycleFilter] = useState('pending');


  // Re-fetch whenever the lifecycle status filter changes (or on initial mount).
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, sla, rej, st, lc, lcs] = await Promise.all([
        axios.get(`${API_URL}/workbench/pending`, { withCredentials: true }),
        axios.get(`${API_URL}/workbench/sla-summary`, { withCredentials: true }),
        axios.get(`${API_URL}/workbench/rejected`, { withCredentials: true }),
        axios.get(`${API_URL}/workbench/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/hr/lifecycle/events?status=${lifecycleFilter}`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/hr/lifecycle/events/stats`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setPending(p.data);
      setSlaSummary(sla.data);
      setRejected(rej.data);
      setStats(st.data);
      setLifecycleEvents(lc.data || []);
      setLifecycleStats(lcs.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [lifecycleFilter]);

  useEffect(() => { fetchAll(); }, [lifecycleFilter, fetchAll]);

  const handleLifecycleAction = async (eventId, action) => {
    try {
      await axios.post(`${API_URL}/hr/lifecycle/events/${eventId}/${action}`, {}, { withCredentials: true });
      toast.success(`Event ${action === 'approve' ? 'approved and applied' : 'rejected'}`);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleApprove = async (type, id) => {
    const collections = { leave: 'leave', regularization: 'regularization', ot: 'ot', reimbursement: 'reimbursement', shift: 'shift', compoff: 'compoff', loan: 'loan' };
    const approvalType = collections[type];
    if (!approvalType) return;
    try {
      await axios.post(`${API_URL}/manager/approve/${approvalType}/${id}`, { action: 'approve', remarks: 'Approved from HR Workbench' }, { withCredentials: true });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleCloseTicket = async (id) => {
    try {
      await axios.patch(`${API_URL}/ess/helpdesk/${id}`, { status: 'closed' }, { withCredentials: true });
      fetchAll();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const slaColor = (s) => ({ ok: 'text-emerald-600', warning: 'text-amber-600', overdue: 'text-red-600' }[s] || 'text-slate-500');
  const slaBg = (s) => ({ ok: 'bg-emerald-50', warning: 'bg-amber-50', overdue: 'bg-red-50' }[s] || 'bg-slate-50');
  const typeColors = { leave: 'bg-emerald-100 text-emerald-700', regularization: 'bg-blue-100 text-blue-700', ot: 'bg-purple-100 text-purple-700', reimbursement: 'bg-orange-100 text-orange-700', shift: 'bg-indigo-100 text-indigo-700', compoff: 'bg-pink-100 text-pink-700', loan: 'bg-red-100 text-red-700', helpdesk: 'bg-teal-100 text-teal-700' };

  const tabs = [
    { key: 'inbox', label: 'Pending Inbox', icon: Inbox, badge: pending.length },
    { key: 'sla', label: 'SLA Aging', icon: Clock, badge: slaSummary?.total_overdue || 0 },
    { key: 'rejected', label: 'Rejected/Rework', icon: XCircle, badge: rejected.length },
    { key: 'lifecycle', label: 'Lifecycle Events', icon: UserCog, badge: lifecycleStats?.pending_total || 0 },
    { key: 'stats', label: 'Overview', icon: BarChart3, badge: 0 },
  ];

  return (
    <MainLayout>
      <div data-testid="hr-workbench-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">HR Workbench</h1>
            <p className="text-sm text-slate-500 mt-1">Centralized inbox, SLA tracking, and escalation management</p>
          </div>
          <div className="flex gap-3">
            <button data-testid="hr-workbench-export-btn" onClick={() => {
              if (pending.length === 0) { toast.error('No pending items to export'); return; }
              downloadCSV(pending.map(p => ({
                Type: p.request_type, Employee: p.employee_name || p.employee_id, Status: p.status, Created: p.created_at, Priority: p.priority || ''
              })), 'hr_workbench_pending.csv');
              toast.success('Pending items exported');
            }} className="flex items-center space-x-2 bg-slate-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-slate-700"><Download className="w-4 h-4" /><span>Export</span></button>
            <button data-testid="refresh-workbench" onClick={fetchAll} className="flex items-center space-x-2 border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm hover:bg-slate-50"><RefreshCw className="w-4 h-4" /><span>Refresh</span></button>
          </div>
        </div>

        {/* Summary Strip */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{pending.length}</p>
            <p className="text-xs text-amber-600">Pending</p>
          </div>
          <div className="bg-red-50 rounded-xl border border-red-200 p-4 text-center">
            <p className="text-2xl font-bold text-red-700">{slaSummary?.total_overdue || 0}</p>
            <p className="text-xs text-red-600">Overdue (&gt;48h)</p>
          </div>
          <div className="bg-orange-50 rounded-xl border border-orange-200 p-4 text-center">
            <p className="text-2xl font-bold text-orange-700">{slaSummary?.total_warning || 0}</p>
            <p className="text-xs text-orange-600">Warning (24-48h)</p>
          </div>
          <div className="bg-purple-50 rounded-xl border border-purple-200 p-4 text-center">
            <p className="text-2xl font-bold text-purple-700">{rejected.length}</p>
            <p className="text-xs text-purple-600">Rejected/Rework</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button key={tab.key} data-testid={`tab-${tab.key}`} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center space-x-2 py-2 px-4 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              <tab.icon className="w-4 h-4" /><span>{tab.label}</span>
              {tab.badge > 0 && <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{tab.badge}</span>}
            </button>
          ))}
        </div>

        {/* INBOX TAB */}
        {activeTab === 'inbox' && (
          <div className="space-y-2">
            {pending.map((item) => (
              <div key={item.id} data-testid={`inbox-item-${item.id}`} className={`bg-white rounded-xl border border-slate-200 p-4 ${slaBg(item.sla_status)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColors[item.request_type] || 'bg-slate-100'}`}>{item.request_label}</span>
                      <span className={`text-xs font-medium ${slaColor(item.sla_status)}`}>
                        {item.sla_status === 'overdue' && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                        {item.sla_hours}h ago
                      </span>
                    </div>
                    <p className="text-sm font-medium text-slate-800">{item.employee_name || item.created_by_name || item.subject || ''}</p>
                    <p className="text-xs text-slate-500">{item.reason || item.description || ''}</p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {item.request_type !== 'helpdesk' ? (
                      <button onClick={() => handleApprove(item.request_type, item.id)} className="bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-emerald-700">Approve</button>
                    ) : (
                      <button onClick={() => handleCloseTicket(item.id)} className="bg-slate-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-slate-700">Close</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {pending.length === 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <h3 className="text-base font-semibold text-slate-600">Inbox Clear</h3>
                <p className="text-sm text-slate-400 mt-1">No pending items to process</p>
              </div>
            )}
          </div>
        )}

        {/* SLA TAB */}
        {activeTab === 'sla' && slaSummary && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100"><h3 className="text-sm font-semibold text-slate-800">SLA Aging by Request Type</h3></div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-4 py-3 text-left font-medium">Type</th>
                <th className="px-4 py-3 text-center font-medium">Pending</th>
                <th className="px-4 py-3 text-center font-medium text-amber-600">Warning (24-48h)</th>
                <th className="px-4 py-3 text-center font-medium text-red-600">Overdue (&gt;48h)</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {slaSummary.items?.map((item, i) => (
                  <tr key={i} data-testid={`sla-row-${i}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-800">{item.type}</td>
                    <td className="px-4 py-3 text-center">{item.pending}</td>
                    <td className="px-4 py-3 text-center"><span className={`${item.warning > 0 ? 'text-amber-600 font-bold' : 'text-slate-400'}`}>{item.warning}</span></td>
                    <td className="px-4 py-3 text-center"><span className={`${item.overdue > 0 ? 'text-red-600 font-bold' : 'text-slate-400'}`}>{item.overdue}</span></td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-bold">
                  <td className="px-4 py-3">TOTAL</td>
                  <td className="px-4 py-3 text-center">{slaSummary.total_pending}</td>
                  <td className="px-4 py-3 text-center text-amber-600">{slaSummary.total_warning}</td>
                  <td className="px-4 py-3 text-center text-red-600">{slaSummary.total_overdue}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* REJECTED TAB */}
        {activeTab === 'rejected' && (
          <div className="space-y-2">
            {rejected.map((item) => (
              <div key={item.id} data-testid={`rejected-item-${item.id}`} className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColors[item.request_type] || 'bg-slate-100'}`}>{item.request_label}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${item.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>{item.status}</span>
                </div>
                <p className="text-sm font-medium text-slate-800">{item.employee_name || item.employee_id}</p>
                <p className="text-xs text-slate-500">{item.reason || item.description || ''}</p>
                {item.approval_remarks && <p className="text-xs text-red-500 mt-1 italic">Remarks: "{item.approval_remarks}"</p>}
                <p className="text-xs text-slate-400 mt-1">By: {item.approved_by_name || 'System'} &middot; {item.approved_at ? new Date(item.approved_at).toLocaleString() : ''}</p>
              </div>
            ))}
            {rejected.length === 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
                <h3 className="text-base font-semibold text-slate-600">No Rejected/Rework Items</h3>
              </div>
            )}
          </div>
        )}

        {/* LIFECYCLE TAB — HR Bench joiner / resignation / hold / update events */}
        {activeTab === 'lifecycle' && (
          <div className="space-y-3" data-testid="lifecycle-panel">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Status:</span>
              {['pending', 'synced', 'rejected'].map((s) => (
                <button
                  key={s}
                  data-testid={`lifecycle-filter-${s}`}
                  onClick={() => setLifecycleFilter(s)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${lifecycleFilter === s ? 'bg-[#0052FF] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                  {s === 'pending' && lifecycleStats?.pending_total > 0 && (
                    <span className="ml-1 bg-red-500 text-white px-1.5 rounded-full text-xs">{lifecycleStats.pending_total}</span>
                  )}
                </button>
              ))}
              <span className="text-xs text-slate-500 ml-auto">{lifecycleEvents.length} events</span>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600"><tr>
                  <th className="px-4 py-3 text-left font-medium">Type</th>
                  <th className="px-4 py-3 text-left font-medium">Employee</th>
                  <th className="px-4 py-3 text-left font-medium">Payload</th>
                  <th className="px-4 py-3 text-left font-medium">Created by</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {lifecycleEvents.map((ev) => {
                    const payloadStr = (() => {
                      try { return JSON.stringify(ev.payload || {}, null, 0); } catch { return ''; }
                    })();
                    const typeColor = {
                      new_joiner: 'bg-emerald-100 text-emerald-700',
                      resignation: 'bg-red-100 text-red-700',
                      hold: 'bg-amber-100 text-amber-800',
                      update: 'bg-blue-100 text-blue-700',
                    }[ev.event_type] || 'bg-slate-100';
                    return (
                      <tr key={ev.id} data-testid={`lifecycle-row-${ev.id}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeColor}`}>
                            {ev.event_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-800">{ev.employee_name || 'Unknown Employee'}</div>
                          {ev.employee_code && <div className="text-[10px] text-slate-500 font-mono">{ev.employee_code}</div>}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600 max-w-md truncate" title={payloadStr}>
                          {payloadStr || ev.reason || '-'}
                        </td>
                        <td className="px-4 py-3 text-xs">{ev.created_by_name || 'system'}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            ev.status === 'pending' ? 'bg-amber-100 text-amber-800'
                            : ev.status === 'synced' ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-red-100 text-red-700'
                          }`}>{ev.status}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {ev.status === 'pending' && (
                            <div className="flex gap-1 justify-end">
                              <button data-testid={`lifecycle-approve-${ev.id}`} onClick={() => handleLifecycleAction(ev.id, 'approve')}
                                className="flex items-center gap-1 px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium">
                                <Check className="w-3 h-3" /> Approve
                              </button>
                              <button data-testid={`lifecycle-reject-${ev.id}`} onClick={() => handleLifecycleAction(ev.id, 'reject')}
                                className="flex items-center gap-1 px-2.5 py-1 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded text-xs font-medium">
                                <X className="w-3 h-3" /> Reject
                              </button>
                            </div>
                          )}
                          {ev.status !== 'pending' && (
                            <span className="text-xs text-slate-400">{ev.approved_by_name || ''}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {lifecycleEvents.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm">No {lifecycleFilter} lifecycle events</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* OVERVIEW TAB */}
        {activeTab === 'stats' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
              <p className="text-3xl font-bold text-slate-900">{stats.active_employees}</p>
              <p className="text-sm text-slate-500 mt-1">Active Employees</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
              <p className="text-3xl font-bold text-emerald-600">{stats.present_today}</p>
              <p className="text-sm text-slate-500 mt-1">Present Today</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
              <p className="text-3xl font-bold text-amber-600">{stats.open_helpdesk_tickets}</p>
              <p className="text-sm text-slate-500 mt-1">Open Helpdesk Tickets</p>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default HRWorkbench;
